<script setup>
import { useMainStore } from '@/store'
import { computed } from 'vue'
import { ARTICLE_TYPE_PRINTABLE } from '@/constants'
import { addLateDeliverySurcharge } from '@/utils/helper'

const inEnglish = computed(() => store.inEnglish)

const displayName = computed(() => {
  return (inEnglish.value) ? props.article.name_en : props.article.name
})

const displayDescription = computed(() => {
  return (inEnglish.value) ? props.article.description_en : props.article.description
})
const store = useMainStore()
// passed properties
const props = defineProps({
  article: {
    type: Object,
    required: true
  },
  daysToGo: {
    type: Number,
    default: 0,
  },
})
// emits
const emit = defineEmits(['remove', 'update', 'add', 'setSquareMeter'])
// computed properties
const earlyBirdMode = computed(() => store.tmp.early_bird_mode)
const lateDelivery = computed(() => store.tmp.late_delivery)

const price = computed(() => {
  return (earlyBirdMode.value)
    ? addLateDeliverySurcharge(props.article.exhibitionPrices.price_net, lateDelivery.value)
    : props.article.exhibitionPrices.price_net
})
// methods
const removeFromBasket = () => {
  emit('remove', props.article)
}

const setSquareMeter = () => {
  emit('setSquareMeter', props.article)
}

const updateBasket = () => {
  emit('update', props.article)
}

const addToBasket = () => {
  emit('add', props.article)
}
</script>

<template>
  <div class="article-inner-frame">
    <div class="article-thumb article-img-slider col-xs-4">
      <div v-if="article.images !== null && article.images[article.currentImg]"
        class="article-img-frame"
        :data-featherlight="'/images/articles/' + article.images[article.currentImg].path"
        :style="{ 'background-image': 'url(/images/articles/' + article.images[article.currentImg].path + ')' }">
        <span v-if="article.images !== null && article.images.length > 1"
          class="prev-img fa fa-angle-left"
          @click="article.currentImg = article.currentImg <= 0 ? article.images.length - 1 : article.currentImg - 1"></span>
        <span v-if="article.images !== null && article.images.length > 1"
          class="next-img fa fa-angle-right"
          @click="article.currentImg = article.currentImg >= article.images.length - 1 ? 0 : article.currentImg + 1"></span>
      </div>
      <div class="article-img-frame" v-else></div>
    </div>
    <div class="article-info col-xs-8">
      <span class="article-label">{{ displayName }}</span>
      <span v-if="article.type === ARTICLE_TYPE_PRINTABLE" class="article-badge">
        {{ $t('labels.printable') }}
      </span>
      <article class="article-description">{{ displayDescription }}</article>
      <div class="action-row row">
        <span class="article-price col-xs-5">
          {{ $filters.formatMoney(price) }}
          <small class="article-unit" :class="article.unit">
            <span class="square_m">/m<sup>2</sup></span>
            <span class="per_unit">{{ $t("booth_articles.per_unit") }}</span>
          </small>
        </span>
        <div v-if="article.pivot.bookable && daysToGo > article.pivot.bookable_till_in_days" class="input-group input-group-sm col-xs-6">
          <span class="input-group-btn">
            <button
              class="btn btn-primary btn-sm"
              tabindex="-1"
              type="button"
              :disabled="article.count <= article.inclusive"
              @click="removeFromBasket">
              <i class="fa fa-minus" aria-hidden="true"></i>
            </button>
          </span>
          <input
            class="form-control"
            type="number"
            :step="1"
            :min="article.inclusive"
            name="depth"
            v-model="article.count"
            @click="setSquareMeter"
            @blur="updateBasket">
          <span class="input-group-btn">
            <button
              class="btn btn-primary btn-sm"
              tabindex="-1"
              type="button"
              @click="addToBasket">
              <i class="fa fa-plus" aria-hidden="true"></i>
            </button>
          </span>
          <span class="input-group-btn" style="font-size:14px;">
            <small class="article-unit" v-if="article.unit === 'sqm'">m<sup>2</sup></small>
            <small class="article-unit" v-if="article.unit === 'pc'">&nbsp;&nbsp;</small>
          </span>
        </div>
        <div v-else class="text-right">
          {{ $t('article_not_available') }}
        </div>
      </div>
    </div>
  </div>
</template>